// /shift-swaps — Shift Swap requests. Staff request to hand a shift to another
// staff member; MANAGER+ approves/rejects. Requesters can cancel their own
// pending requests. Approving reassigns the linked attendance record.

import { useMemo, useState } from 'react';
import {
  PageContainer,
  ErrorBoundary,
  DataTable,
  Badge,
  Button,
  Select,
  Skeleton,
  EmptyState,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';

type BadgeVariant = 'success' | 'danger' | 'warning' | 'info' | 'draft' | 'neutral';
import {
  useShiftSwaps,
  useApproveSwap,
  useRejectSwap,
  useCancelSwap,
} from '@/features/shifts';
import type { ShiftSwapRequest, SwapStatus } from '@/features/shifts';
import { useAuthStore } from '@/stores/use-auth-store';
import { ApiError } from '@/lib/api-client';
import { RequestSwapModal } from './RequestSwapModal';

const ROLE_LEVELS: Record<string, number> = {
  SUPER_ADMIN: 100, ADMIN: 80, MODERATOR: 60, OWNER: 50, MANAGER: 30, CASHIER: 5,
};

const STATUS_META: Record<SwapStatus, { label: string; variant: BadgeVariant }> = {
  PENDING: { label: 'Pending', variant: 'warning' },
  APPROVED: { label: 'Approved', variant: 'success' },
  REJECTED: { label: 'Rejected', variant: 'danger' },
  CANCELLED: { label: 'Cancelled', variant: 'neutral' },
};

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

function ShiftSwapsContent() {
  const { toast } = useToast();
  const role = useAuthStore((s) => s.profile?.role ?? '');
  const userId = useAuthStore((s) => s.profile?.id ?? '');
  const tenantId = useAuthStore((s) => s.profile?.tenant?.id);
  const canManage = (ROLE_LEVELS[role] ?? 0) >= ROLE_LEVELS.MANAGER;

  const [statusFilter, setStatusFilter] = useState<string>('PENDING');
  const [requestOpen, setRequestOpen] = useState(false);

  const { data, isLoading } = useShiftSwaps({
    status: (statusFilter || undefined) as SwapStatus | undefined,
    page: 1,
  });

  const approve = useApproveSwap();
  const reject = useRejectSwap();
  const cancel = useCancelSwap();

  async function run(action: () => Promise<unknown>, successMsg: string) {
    try {
      await action();
      toast({ title: successMsg, variant: 'success' });
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Action failed', variant: 'error' });
    }
  }

  const rows = data?.data ?? [];

  const columns: ColumnDef<ShiftSwapRequest>[] = useMemo(
    () => [
      {
        key: 'shift_date',
        header: 'Date',
        render: (_v, r) => <span className="text-sm tabular-nums">{r.shift_date}</span>,
      },
      {
        key: 'requester_user_id',
        header: 'From',
        render: (_v, r) => <span className="text-sm font-mono">{r.requester_user_id.slice(0, 8)}</span>,
      },
      {
        key: 'target_user_id',
        header: 'To',
        render: (_v, r) => <span className="text-sm font-mono">{r.target_user_id.slice(0, 8)}</span>,
      },
      {
        key: 'reason',
        header: 'Reason',
        render: (_v, r) => <span className="text-sm text-neutral-600 dark:text-neutral-300">{r.reason ?? '—'}</span>,
      },
      {
        key: 'status',
        header: 'Status',
        render: (_v, r) => {
          const m = STATUS_META[r.status];
          return <Badge variant={m.variant} size="sm">{m.label}</Badge>;
        },
      },
      {
        key: 'actions',
        header: '',
        render: (_v, r) => {
          if (r.status !== 'PENDING') return null;
          return (
            <div className="flex items-center gap-1.5 justify-end">
              {canManage && (
                <>
                  <Button variant="primary" size="sm"
                    onClick={() => run(() => approve.mutateAsync(r.id), 'Swap approved — shift reassigned')}>
                    Approve
                  </Button>
                  <Button variant="ghost" size="sm"
                    onClick={() => run(() => reject.mutateAsync(r.id), 'Swap rejected')}>
                    Reject
                  </Button>
                </>
              )}
              {r.requester_user_id === userId && (
                <Button variant="ghost" size="sm"
                  onClick={() => run(() => cancel.mutateAsync(r.id), 'Request cancelled')}>
                  Cancel
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [canManage, userId, approve, reject, cancel],
  );

  return (
    <div className="animate-fade-in">
      <div className="mb-4 flex items-center justify-between gap-3">
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          {canManage ? 'Approve or reject staff shift-swap requests.' : 'Your shift-swap requests.'}
        </p>
        <div className="flex items-center gap-2">
          <div className="w-48">
            <Select value={statusFilter} onChange={(v) => setStatusFilter(typeof v === 'string' ? v : '')} options={STATUS_FILTERS} />
          </div>
          <Button variant="primary" size="sm" onClick={() => setRequestOpen(true)}>
            + Request Swap
          </Button>
        </div>
      </div>

      {isLoading ? (
        <Skeleton variant="rect" height={240} className="rounded-lg" />
      ) : rows.length === 0 ? (
        <EmptyState
          variant="page"
          title="No swap requests"
          description="Create one with “Request Swap”, or requests raised by staff will appear here."
        />
      ) : (
        <DataTable<ShiftSwapRequest> data={rows} columns={columns} />
      )}

      <RequestSwapModal open={requestOpen} onClose={() => setRequestOpen(false)} tenantId={tenantId} currentUserId={userId} />
    </div>
  );
}

export function ShiftSwapsPage() {
  return (
    <PageContainer title="Shift Swaps" breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Shift Swaps' }]}>
      <ErrorBoundary resetKey="shift-swaps">
        <ShiftSwapsContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
